import { useState, useEffect, useRef } from "react";
import { ComboPopup } from "../components/Engagement";
import { italianQuizEasy, italianQuizHard } from "./ItalianQuizData";

export function ItalianQuizGame({ t, lang, ageGroup, onBack, onComplete, playSound }) {
  const quizData = ageGroup === "6-7" ? italianQuizEasy : italianQuizHard;
  
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const maxComboRef = useRef(0);
  const [feedback, setFeedback] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  useEffect(() => {
    // Shuffle and pick 10 questions
    const shuffled = [...quizData].sort(() => Math.random() - 0.5);
    setQuestions(shuffled.slice(0, 10));
  }, [quizData]);

  const handleAnswer = (answerIndex) => {
    if (feedback !== null) return;
    
    const currentQuestion = questions[currentIndex];
    const isCorrect = answerIndex === currentQuestion.answer;
    if (!isCorrect) setCombo(0);
    
    setFeedback(isCorrect ? "correct" : "wrong");
    setShowExplanation(true);
    
    if (isCorrect) {
      setScore(score + 1);
      maxComboRef.current = Math.max(maxComboRef.current, combo + 1);
      setCombo(combo + 1);
      if (playSound) playSound("correct");
    } else {
      if (playSound) playSound("wrong");
    }
    
    setTimeout(() => {
      setFeedback(null);
      setShowExplanation(false);
      
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setGameOver(true);
        if (playSound) playSound("complete");
        if (onComplete) {
          onComplete({ score: score + (isCorrect ? 1 : 0), total: questions.length, maxCombo: maxComboRef.current });
        }
      }
    }, 2500);
  };

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 flex items-center justify-center">
        <div className="text-white text-2xl">Caricamento...</div>
      </div>
    );
  }

  if (gameOver) {
    const percentage = Math.round((score / questions.length) * 100);
    let medal = null;
    if (percentage >= 90) medal = "🥇";
    else if (percentage >= 70) medal = "🥈";
    else if (percentage >= 50) medal = "🥉";
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
          <div className="text-6xl mb-4">{medal || "📚"}</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            {lang === "it" ? "Quiz Completato!" : "Quiz Complete!"}
          </h2>
          <div className="text-5xl font-bold text-green-600 mb-4">
            {score}/{questions.length}
          </div>
          <div className="text-xl text-gray-600 mb-6">
            {percentage}% {lang === "it" ? "corretto" : "correct"}
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={onBack}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold"
            >
              {t.backHome}
            </button>
            <button
              onClick={() => {
                setCurrentIndex(0);
                setScore(0);
                setGameOver(false);
                const shuffled = [...quizData].sort(() => Math.random() - 0.5);
                setQuestions(shuffled.slice(0, 10));
              }}
              className="flex-1 py-3 rounded-xl border-2 border-green-500 text-green-600 font-bold"
            >
              {t.playAgain}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-4">
      <div className="max-w-lg mx-auto">
        <button
          onClick={onBack}
          className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4"
        >
          ← {t.backHome}
        </button>
        
        <ComboPopup combo={combo} />
        <h2 className="text-2xl font-bold text-white text-center mb-2">🇮🇹 Quiz Italiano</h2>
        
        {/* Progress */}
        <div className="flex justify-between text-white mb-4">
          <span>{t.question} {currentIndex + 1}/{questions.length}</span>
          <span>{t.score}: {score}</span>
        </div>
        
        <div className="h-2 bg-white/30 rounded-full mb-6">
          <div 
            className="h-full bg-white rounded-full transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
        
        {/* Question Card */}
        <div className={`bg-white rounded-3xl p-6 shadow-xl ${feedback === "correct" ? "ring-4 ring-green-400" : feedback === "wrong" ? "ring-4 ring-red-400" : ""}`}>
          {/* Question Type Badge */}
          <div className="mb-4">
            <span className="bg-green-100 text-green-700 text-sm px-3 py-1 rounded-full font-bold">
              {currentQuestion.type.replace("_", " ").toUpperCase()}
            </span>
          </div>
          
          {/* Question */}
          <p className="text-xl text-gray-800 font-bold mb-6 text-center">
            {lang === "it" ? currentQuestion.question_it : currentQuestion.question_en}
          </p>
          
          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                disabled={feedback !== null}
                className={`
                  w-full py-4 px-6 rounded-xl text-lg font-bold transition-all
                  ${feedback === null 
                    ? "bg-gradient-to-r from-green-400 to-emerald-500 text-white hover:scale-102" 
                    : idx === currentQuestion.answer
                      ? "bg-green-500 text-white ring-4 ring-green-300"
                      : feedback === "wrong" && idx === currentQuestion.options.indexOf(currentQuestion.options[idx])
                        ? "bg-gray-200 text-gray-500"
                        : "bg-gray-200 text-gray-500"
                  }
                `}
              >
                {option}
              </button>
            ))}
          </div>
          
          {/* Explanation */}
          {showExplanation && (
            <div className={`mt-4 p-4 rounded-xl ${feedback === "correct" ? "bg-green-50" : "bg-red-50"}`}>
              <div className={`text-center font-bold mb-2 ${feedback === "correct" ? "text-green-600" : "text-red-600"}`}>
                {feedback === "correct" ? `✅ ${t.correct}` : `❌ ${t.wrong}`}
              </div>
              <p className="text-gray-700 text-center">
                {lang === "it" ? currentQuestion.explanation_it : currentQuestion.explanation_en}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ItalianQuizGame;
